import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { PageHeader } from '../../components/PageHeader';
import { layoutVisibilityApi } from '../../api/layoutVisibilityApi';
import type {
  BoardLayout,
  LayoutVisibilityConfig,
  PublicResultsLayout,
} from '../../types/domain';
import {
  BOARD_LAYOUT_DESCRIPTIONS,
  BOARD_LAYOUT_LABELS,
  PUBLIC_LAYOUT_DESCRIPTIONS,
  PUBLIC_LAYOUT_LABELS,
} from '../../types/domain';
import { LayoutPreview } from '../../components/results/LayoutPreview';
import { BoardLayoutPreview } from '../../components/results/BoardLayoutPreview';
import { LayoutPreviewDialog } from '../../components/admin/LayoutPreviewDialog';

const PUBLIC_LAYOUTS: PublicResultsLayout[] = ['CLASSIC', 'PODIUM', 'SPLIT_VIEW'];
const BOARD_LAYOUTS: BoardLayout[] = ['MATRIX', 'FOCUS', 'CAROUSEL', 'SPLIT', 'COLUMNS'];

export const LayoutVisibilityPage = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin', 'layout-visibility'],
    queryFn: layoutVisibilityApi.superadminGet,
  });

  const [draft, setDraft] = useState<LayoutVisibilityConfig | null>(null);
  const [previewPublic, setPreviewPublic] = useState<PublicResultsLayout | null>(null);
  const [previewBoard, setPreviewBoard] = useState<BoardLayout | null>(null);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const isDirty = useMemo(() => {
    if (!data || !draft) return false;
    return JSON.stringify(data) !== JSON.stringify(draft);
  }, [data, draft]);

  const updateMutation = useMutation({
    mutationFn: (payload: LayoutVisibilityConfig) => layoutVisibilityApi.update(payload),
    onSuccess: (res) => {
      enqueueSnackbar('Nastavení uloženo', { variant: 'success' });
      queryClient.setQueryData(['superadmin', 'layout-visibility'], res);
      queryClient.invalidateQueries({ queryKey: ['layout-visibility'] });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při ukládání', { variant: 'error' }),
  });

  if (isLoading || !draft) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const togglePublic = (layout: PublicResultsLayout, visible: boolean) => {
    setDraft((d) =>
      d ? { ...d, publicLayouts: { ...d.publicLayouts, [layout]: visible } } : d,
    );
  };
  const toggleBoard = (layout: BoardLayout, visible: boolean) => {
    setDraft((d) =>
      d ? { ...d, boardLayouts: { ...d.boardLayouts, [layout]: visible } } : d,
    );
  };

  const publicDefaultHidden = !draft.publicLayouts[draft.publicDefault];
  const boardDefaultHidden = !draft.boardLayouts[draft.boardDefault];

  const canSave = isDirty && !publicDefaultHidden && !boardDefaultHidden && !updateMutation.isPending;

  return (
    <>
      <Helmet>
        <title>Viditelnost layoutů | SUPER ADMIN</title>
      </Helmet>
      <PageHeader
        title="Viditelnost layoutů"
        subtitle="Globálně zapnout/vypnout konkrétní layouty a zvolit výchozí. Skrytý layout admin neuvidí v nabídce, již uložené soutěže dostanou fallback na výchozí."
      />

      <Stack spacing={4}>
        {(publicDefaultHidden || boardDefaultHidden) && (
          <Alert severity="warning" variant="outlined">
            Výchozí layout nesmí být skrytý. Zapni viditelnost nebo vyber jiný výchozí layout.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Layouty pro diváky
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              {PUBLIC_LAYOUTS.map((l) => (
                <LayoutCard
                  key={l}
                  visible={draft.publicLayouts[l] ?? true}
                  isDefault={draft.publicDefault === l}
                  onToggle={(v) => togglePublic(l, v)}
                  onPreview={() => setPreviewPublic(l)}
                  label={PUBLIC_LAYOUT_LABELS[l]}
                  description={PUBLIC_LAYOUT_DESCRIPTIONS[l]}
                  preview={<LayoutPreview layout={l} size={70} />}
                />
              ))}
            </Box>

            <FormControl sx={{ maxWidth: 360 }} fullWidth>
              <InputLabel id="public-default-label">Výchozí layout pro diváky</InputLabel>
              <Select
                labelId="public-default-label"
                label="Výchozí layout pro diváky"
                value={draft.publicDefault}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, publicDefault: e.target.value as PublicResultsLayout } : d,
                  )
                }
              >
                {PUBLIC_LAYOUTS.map((l) => (
                  <MenuItem key={l} value={l} disabled={!draft.publicLayouts[l]}>
                    {PUBLIC_LAYOUT_LABELS[l]}
                    {!draft.publicLayouts[l] && ' (skrytý)'}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Použije se jako fallback při skrytí uloženého layoutu i pro nové soutěže.
              </Typography>
            </FormControl>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Layouty pro plat / televizi / projektor
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              {BOARD_LAYOUTS.map((l) => (
                <LayoutCard
                  key={l}
                  visible={draft.boardLayouts[l] ?? true}
                  isDefault={draft.boardDefault === l}
                  onToggle={(v) => toggleBoard(l, v)}
                  onPreview={() => setPreviewBoard(l)}
                  label={BOARD_LAYOUT_LABELS[l]}
                  description={BOARD_LAYOUT_DESCRIPTIONS[l]}
                  preview={<BoardLayoutPreview layout={l} size={72} />}
                />
              ))}
            </Box>

            <FormControl sx={{ maxWidth: 360 }} fullWidth>
              <InputLabel id="board-default-label">Výchozí layout pro plat / televizi</InputLabel>
              <Select
                labelId="board-default-label"
                label="Výchozí layout pro plat / televizi"
                value={draft.boardDefault}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, boardDefault: e.target.value as BoardLayout } : d,
                  )
                }
              >
                {BOARD_LAYOUTS.map((l) => (
                  <MenuItem key={l} value={l} disabled={!draft.boardLayouts[l]}>
                    {BOARD_LAYOUT_LABELS[l]}
                    {!draft.boardLayouts[l] && ' (skrytý)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        <Divider />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!canSave}
            onClick={() => updateMutation.mutate(draft)}
          >
            {updateMutation.isPending ? 'Ukládám…' : 'Uložit'}
          </Button>
        </Stack>
      </Stack>

      {previewPublic && (
        <LayoutPreviewDialog
          kind="public"
          layout={previewPublic}
          open
          onClose={() => setPreviewPublic(null)}
        />
      )}
      {previewBoard && (
        <LayoutPreviewDialog
          kind="board"
          layout={previewBoard}
          open
          onClose={() => setPreviewBoard(null)}
        />
      )}
    </>
  );
};

interface LayoutCardProps {
  visible: boolean;
  isDefault: boolean;
  onToggle: (v: boolean) => void;
  onPreview: () => void;
  label: string;
  description: string;
  preview: React.ReactNode;
}

const LayoutCard = ({ visible, isDefault, onToggle, onPreview, label, description, preview }: LayoutCardProps) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      opacity: visible ? 1 : 0.55,
      borderColor: isDefault ? 'primary.main' : 'divider',
      borderWidth: isDefault ? 2 : 1,
    }}
  >
    {preview}
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography fontWeight={700} sx={{ flex: 1 }}>{label}</Typography>
      {isDefault && (
        <Typography variant="caption" color="primary.main" fontWeight={700}>
          VÝCHOZÍ
        </Typography>
      )}
    </Stack>
    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
      {description}
    </Typography>
    <FormControlLabel
      control={<Switch checked={visible} onChange={(e) => onToggle(e.target.checked)} />}
      label={visible ? 'Viditelný' : 'Skrytý'}
    />
    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={onPreview}>
      Ukázka
    </Button>
  </Paper>
);
